/**
 * The frontend mirror of the backend `PermissionScope` (ADR 0007 /
 * `plans/permission-scope.md`). A grant — and the target of a permission check — is
 * scoped either franchise-wide (holds across the whole franchise) or at one venue.
 */
export type PermissionScope = { kind: 'franchise_wide' } | { kind: 'venue'; venueId: string };

/** A resolved permission grant: a `code` that holds at a `scope`. */
export type Grant = { code: string; scope: PermissionScope };

/**
 * The single resolution rule, unioned across all grants — mirrors the Kotlin
 * `Set<GrantedPermission>.permits` table (ADR 0007):
 *  - a franchise-wide grant holds at every venue AND franchise-wide;
 *  - a venue grant holds only at that venue, never franchise-wide;
 *  - a franchise-wide check is satisfied only by a franchise-wide grant.
 *
 * This gates UI controls; the backend 403 stays authoritative.
 */
export function permits(grants: Grant[], code: string, scope: PermissionScope): boolean {
  if (grants.some((g) => g.code === code && g.scope.kind === 'franchise_wide')) return true;
  return (
    scope.kind === 'venue' &&
    grants.some(
      (g) => g.code === code && g.scope.kind === 'venue' && g.scope.venueId === scope.venueId,
    )
  );
}
